/**
 * genreService — best-effort genre lookup for the guest PWA.
 *
 * YouTube requests are written with a blank genre because the app can't read
 * ID3 tags. This calls the `guessGenre` Cloud Function (Last.fm → MusicBrainz →
 * Spotify, server-cached) and backfills the genre onto the request doc(s) once
 * it resolves — so PWA-only requests no longer land as "Unknown Genre" while
 * waiting for a desktop to process them.
 *
 * Fire-and-forget: the request is already saved before this runs; a genre
 * failure never blocks or throws. Mirrors the desktop genre-guess.js.
 */

import { updateDoc } from "firebase/firestore";

const GUESS_GENRE_URL =
  process.env.REACT_APP_GUESS_GENRE_URL ||
  "https://us-central1-chatbout001.cloudfunctions.net/guessGenre";

// In-session memo so the same track requested twice doesn't re-hit the function
// (the function is also server-cached; this just saves the round-trip).
const _memo = new Map();

/** Resolve a genre string for a track, or null. Never throws. */
export async function guessGenreRemote(title, artist) {
  const t = String(title || "").trim();
  const a = String(artist || "").trim();
  if (!t || !a) return null;

  const key = `${t.toLowerCase()}|${a.toLowerCase()}`;
  if (_memo.has(key)) return _memo.get(key);

  try {
    const res = await fetch(GUESS_GENRE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title: t, artist: a }),
    });
    if (!res.ok) {
      _memo.set(key, null);
      return null;
    }
    const data = await res.json().catch(() => ({}));
    const genre =
      data && typeof data.genre === "string" && data.genre.trim()
        ? data.genre.trim()
        : null;
    _memo.set(key, genre);
    return genre;
  } catch (_) {
    return null;
  }
}

/**
 * Resolve the genre and write it into the given doc fields.
 * @param {Object} opts
 * @param {string} opts.title
 * @param {string} opts.artist
 * @param {string} [opts.currentGenre]  skip entirely if the track already has a real genre
 * @param {Array<{ref: any, field: string}>} opts.targets  docs+fields to patch (field may be a dot-path, e.g. "request.genre")
 * @returns {Promise<string|null>} the resolved genre, or null
 */
export async function backfillGenre({ title, artist, currentGenre = "", targets = [] }) {
  if (currentGenre && String(currentGenre).trim()) return null; // don't clobber a real genre
  const genre = await guessGenreRemote(title, artist);
  if (!genre) return null;
  await Promise.all(
    (targets || [])
      .filter((t) => t && t.ref && t.field)
      .map((t) => updateDoc(t.ref, { [t.field]: genre }).catch(() => {}))
  );
  return genre;
}
